import React, { useCallback } from "react";
import { FormProvider, useFieldArray, useForm } from "react-hook-form";
import { FormHelperText, Stack } from "@mui/material";
import { yupResolver } from "@hookform/resolvers/yup";
import { DragDropContext, Droppable } from "react-beautiful-dnd";

import WorkflowSteps from "./WorkflowSteps";
import {
  createWorkflow,
  updateWorkflow,
} from "../../../../../sevices/apiWorkflows";
import { useResponsive } from "../../../../../hooks/useResponsive";
import FormDialog from "../../../../../components/Dialog/FormDialog";
import { RFHTextField } from "../../../../../components/RFH";
import { workflowSchema } from "../../../../../schemas/createWorkFlow";
import useMutate from "../../../../../hooks/useMutate";
import { AddRoundedIcon } from "../../../../../utils/constants/icons";

function CreateWorkflow({
  open,
  close,
  workflowData = null,
  edit= false,
  workflowId = "",
}) {
  const isMobile = useResponsive("down", "md");
  const {
    control,
    register,
    handleSubmit,
    formState: { errors },
    setError,
    clearErrors,
    setValue,
    ...methods
  } = useForm({
    resolver: yupResolver(workflowSchema),
    defaultValues: {
      workflow_name: workflowData?.col1 ? workflowData?.col1 : "",
      workflow_steps: workflowData?.col2
        ? workflowData.col2.map((step, index) => ({
            status: step.status,
            workflow_step_id: step.workflowstep_id,
            is_completed: step.is_completed,
            generic_step_id: step.generic_step_id,
          }))
        : [],
    },
  });

  const { fields, append, remove, move } = useFieldArray({
    control,
    name: "workflow_steps",
  });

  const handleOnDragEnd = useCallback(
    (result) => {
      if (!result.destination) return;

      const items = Array.from(fields);
      const [reorderedItem] = items.splice(result.source.index, 1);
      items.splice(result.destination.index, 0, reorderedItem);

      move(result.source.index, result.destination.index);
    },
    [fields, move]
  );

  const { mutate: createWorkflowmutate } = useMutate(createWorkflow,["adminWorkflow"], 
    () => {},
    () => {
      close(false);
    });

  const { mutate: updateWorkflowmutate } = useMutate(updateWorkflow,["adminWorkflow"],
    () => {},
    () => {
      close(false);
    });

  const onSubmit = useCallback(
    (data) => {
      const { workflow_name, workflow_steps } = data;
      if (workflow_steps.length === 0) {
        setError("workflow_steps", {
          type: "manual",
          message: "At least one workflow step is required.",
        });
        return;
      }
      if (edit) {
        const mappedSteps = workflow_steps.map((step, index) => ({
          ...step,
          step_sequence: index + 1,
          status: step.status,
          generic_step_id: step.generic_step_id,
        }));

        const payload = {
          workflow_name,
          workflow_steps: mappedSteps,
        };

        updateWorkflowmutate({ workflow_id: workflowId, data: payload });
      } else {
        const mappedSteps = workflow_steps.map((step, index) => ({
          ...step,
          step_sequence: index + 1,
          status: step.status,
        }));

        const payload = {
          workflow_name,
          workflow_steps: mappedSteps,
        };
        createWorkflowmutate(payload);
      }
    },
    [edit, workflowId, updateWorkflowmutate, createWorkflowmutate]
  );

  return (
    <React.Fragment>
      <FormProvider control={control} {...methods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <FormDialog
            open={open}
            handleClose={() => close(false)}
            title={edit ? <p>Edit Workflow</p> : <p>Create Workflow</p>}
            buttonTxt={edit ? "Update" : "Create"}
            handleSubmit={handleSubmit(onSubmit)}
            className="create-workflow-dialog"
            PaperProps={{
              className: "create-workflow-paper",
            }}
            cancelBtn={true}
            fullWidth={true}
            width={470}
          >
            <div>
              <div className="workflowName">
                <p>Workflow Name</p>
                <RFHTextField
                  id="outlined-basic"
                  variant="outlined"
                  name="workflow_name"
                  className="workflow-input"
                  defaultPadding={false}
                  inputProps={{
                    padding: isMobile && "8.5px 12px",
                  }}
                  FormHelperTextProps={{
                    className: "helper-text",
                  }}
                  required
                />
              </div>
              <Stack
                direction="row"
                justifyContent={!isMobile && "space-evenly"}
                alignItems="start"
              >
                <p className="workflow-step-names">Workflow Steps</p>
                <p className="workflow-step-names">Generic Steps</p>
              </Stack>
              <DragDropContext onDragEnd={handleOnDragEnd}>
                <Droppable droppableId="dragbaleSteps">
                  {(provided) => (
                    <div
                      className="addWorkflowSteps"
                      {...provided.droppableProps}
                      ref={provided.innerRef}
                    >
                      <WorkflowSteps
                        fields={fields}
                        remove={remove}
                        register={register}
                        errors={errors}
                        edit={edit}
                        close={close}
                        control={control}
                        setValue={setValue}
                      />
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              </DragDropContext>

              <div className="addWorkflowSteps cursor-pointer">
                <div
                  className="addWorkflowstepButton"
                  onClick={() => {
                    append({
                      is_completed: false,
                      status: "",
                      workflow_step_id: null,
                      generic_step_id: null,
                    });
                    clearErrors("workflow_steps");
                  }}
                >
                  <AddRoundedIcon className="add-icon"/>
                  <span>Add Step</span>
                </div>
              </div>
              <FormHelperText
                error={!!errors?.workflow_steps?.message}
                className="error-text"
              >
                {errors?.workflow_steps?.message}
              </FormHelperText>
            </div>
          </FormDialog>
        </form>
      </FormProvider>
    </React.Fragment>
  );
}

export default React.memo(CreateWorkflow);
